import React, { useEffect, useState } from "react";
import { Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/UI";

export default function Brands() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brands");
      setBrands(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post("/brands", { name: name.trim(), description: description.trim() });
      toast.success(`Added "${name.trim()}"`);
      setName(""); setDescription("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        testid="brands-header"
        title="Brand portfolio"
        subtitle="The bottles you're moving. Tag accounts with what you pitch."
      />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card p-6 lg:col-span-2">
          {loading ? (
            <Spinner />
          ) : brands.length === 0 ? (
            <EmptyState
              testid="empty-brands"
              icon={Tag}
              title="No brands yet"
              body="Add your first label and start associating it with accounts."
            />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="brands-grid">
              {brands.map((b) => (
                <div key={b.id} className="rounded-2xl border border-brand-border p-4 hover:bg-brand-muted/30 transition" data-testid={`brand-${b.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-brand-ink text-white flex items-center justify-center">
                      <Tag size={16} />
                    </div>
                    <div className="font-display text-lg text-brand-ink">{b.name}</div>
                  </div>
                  {b.description && (
                    <p className="text-sm text-brand-mutedFg mt-2 line-clamp-3">{b.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="card p-6 space-y-3" data-testid="brand-create-form">
          <div className="label">New brand</div>
          <input
            className="input-base"
            placeholder="e.g. Literally Vodka"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="brand-name"
            required
          />
          <textarea
            className="input-base min-h-[80px]"
            placeholder="Optional description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="brand-description"
          />
          <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="brand-save">
            <Plus size={16} /> {saving ? "Adding…" : "Add brand"}
          </button>
        </form>
      </div>
    </div>
  );
}
